import React, { Component } from "react";
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
} from "@mui/material";
import OndemandVideoRoundedIcon from "@mui/icons-material/OndemandVideoRounded";
import PsychologyAltRoundedIcon from "@mui/icons-material/PsychologyAltRounded";
import TouchAppRoundedIcon from "@mui/icons-material/TouchAppRounded";
import Header from "../header_h/Header";
import Footer from "../footer/Footer";
import BreadcrumbsModulePage from "../breadcrumbs/BreadcrumbsModulePage";
import SelectionContainer from "../selection_container/SelectionContainer.js";
import ModuleContainer from "../module-container/ModuleContainer";
import ExportPdfButton from "./ExportPdfButton.js";
import { QUESTION_KIND, resolveQuestionKind } from "./runtime/questionKinds";
import "../selection_container/SelectionContainer.css";

const sectionIcon = (iconKey, idx) => {
  if (iconKey === "test") return <PsychologyAltRoundedIcon />;
  if (iconKey === "interactive") return <TouchAppRoundedIcon />;
  if (iconKey === "video") return <OndemandVideoRoundedIcon />;

  const fallbackIcons = [
    <PsychologyAltRoundedIcon />,
    <TouchAppRoundedIcon />,
    <OndemandVideoRoundedIcon />,
  ];
  return fallbackIcons[idx % fallbackIcons.length];
};

function matchesSelection(selectionType, question) {
  const kind = resolveQuestionKind(question);

  if (selectionType === "test") {
    return kind === QUESTION_KIND.TEST || kind === QUESTION_KIND.ONLY_TEXT;
  }

  if (selectionType === "interactive") {
    return (
      kind === QUESTION_KIND.DROPDOWN ||
      kind === QUESTION_KIND.DROPDOWN2 ||
      kind === QUESTION_KIND.DND ||
      kind === QUESTION_KIND.DND2 ||
      kind === QUESTION_KIND.FILLWORD ||
      kind === QUESTION_KIND.PUZZLE ||
      kind === QUESTION_KIND.WORD_GRID
    );
  }

  if (selectionType === "video") {
    return kind === QUESTION_KIND.VIDEO;
  }

  return false;
}

function getSectionSlug(section) {
  return String(section?.sectionSlug || section?.slug || "").trim();
}

function getQuestionSectionSlug(question) {
  return String(question?.sectionSlug || "").trim();
}

class ModulePage extends Component {
  state = {
    selectedType: null,
    selectedSectionSlug: null,
    filteredModule: null,
  };

  componentDidUpdate(prevProps) {
    const prevTitle = prevProps?.module?.title;
    const nextTitle = this.props?.module?.title;
    const prevSlug = prevProps?.module?.moduleSlug;
    const nextSlug = this.props?.module?.moduleSlug;

    if (prevTitle !== nextTitle || prevSlug !== nextSlug) {
      this.setState({
        selectedType: null,
        selectedSectionSlug: null,
        filteredModule: null,
      });
    }
  }

  handleSelect = (type) => {
    const { module } = this.props;
    const questions = Array.isArray(module?.questions) ? module.questions : [];

    const filteredQuestions = questions.filter((question) =>
      matchesSelection(type, question)
    );

    const filteredModule = {
      ...module,
      questions: filteredQuestions,
    };

    this.setState({ selectedType: type, filteredModule });
  };

  handleSectionSelect = (section) => {
    const { module } = this.props;
    const questions = Array.isArray(module?.questions) ? module.questions : [];
    const sectionSlug = getSectionSlug(section);

    const filteredModule = {
      ...module,
      title: section?.title ? `${module?.title || ""}: ${section.title}` : module?.title,
      questions: questions.filter((question) => getQuestionSectionSlug(question) === sectionSlug),
    };

    this.setState({ selectedSectionSlug: sectionSlug, filteredModule });
  };

  render() {
    const { userName, userRoles, module, path } = this.props;
    const { component: Component } = this.props;
    const { selectedType, selectedSectionSlug, filteredModule } = this.state;

    const isAvrRoute = window.location.pathname.includes("/avr/");
    const questions = Array.isArray(module?.questions) ? module.questions : [];
    const sectionQuestionSlugs = new Set(
      questions.map(getQuestionSectionSlug).filter(Boolean)
    );
    const moduleSections = Array.isArray(module?.sections)
      ? module.sections
          .filter((section) => {
            const sectionSlug = getSectionSlug(section);
            return section && section.enabled !== false && sectionSlug && sectionQuestionSlugs.has(sectionSlug);
          })
          .sort((a, b) => Number(a.order ?? 0) - Number(b.order ?? 0))
      : [];

    if (!isAvrRoute) {
      return (
        <>
          <Header userName={userName} userRoles={userRoles} />
          <BreadcrumbsModulePage moduleTitle={module?.title} />
          {Array.isArray(userRoles) && userRoles.includes("ADMIN") && (
            <ExportPdfButton module={module} />
          )}

          {moduleSections.length > 0 && !selectedSectionSlug ? (
            <Box className="module-type-page" sx={{ maxWidth: 560, mx: "auto" }}>
              <Paper className="module-type-panel" variant="outlined">
                <Typography variant="h5" component="h1">
                  Оберіть тип завдань
                </Typography>
                <List disablePadding className="module-type-list">
                  {moduleSections.map((section, idx) => (
                    <ListItemButton
                      key={section.sectionSlug || section.slug}
                      className="module-type-list__item"
                      onClick={() => this.handleSectionSelect(section)}
                    >
                      <ListItemIcon>{sectionIcon(section.iconKey, idx)}</ListItemIcon>
                      <ListItemText
                        primary={section.title || section.sectionSlug || section.slug}
                        secondary={section.description || null}
                      />
                    </ListItemButton>
                  ))}
                </List>
              </Paper>
            </Box>
          ) : moduleSections.length > 0 && filteredModule ? (
            <>
              {filteredModule.questions.length > 0 ? (
                <ModuleContainer component={Component} module={filteredModule} path={path} />
              ) : (
                <div className="placeholder-content">
                  <p>Для даної секції завдання не призначено</p>
                </div>
              )}
            </>
          ) : (
            <ModuleContainer component={Component} module={module} path={path} />
          )}
          <Footer />
        </>
      );
    }

    return (
      <>
        <Header userName={userName} userRoles={userRoles} />
        <BreadcrumbsModulePage />
        {Array.isArray(userRoles) && userRoles.includes("ADMIN") && (
          <ExportPdfButton module={module} />
        )}

        {!selectedType ? (
          <SelectionContainer onSelect={this.handleSelect} />
        ) : filteredModule && filteredModule.questions.length > 0 ? (
          <ModuleContainer
            component={Component}
            module={filteredModule}
            path={path}
          />
        ) : (
          <div className="placeholder-content">
            <p>Для даної теми завдання не призначено</p>
          </div>
        )}
        <Footer />
      </>
    );
  }
}

export default ModulePage;
